import { GetStaticPaths, GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import { API_URL, SITE_URL } from '@goodfellas/environment'

import { goodfellasApi } from '../../apis'
import { AppGateOverlay } from '../../components/app-gate/AppGateOverlay'
import { AppleSmartBanner } from '../../components/app-gate/AppleSmartBanner'
import EditProfileModal from '../../components/public-profiles/EditProfileModal'
import GymProfileView from '../../components/public-profiles/GymProfileView'
import ProfileNotFound from '../../components/public-profiles/ProfileNotFound'
import { gymUrl } from '../../constants/shareLinks'
import { useAuth } from '../../contexts/AuthContext'
import { CompetitionMatch } from '../../types/competition'

export interface ProfileData {
  id: string
  type: 'FIGHTER' | 'GYM'
  name: string
  avatar: string | null
  bio?: string
  instagram?: string
  achievements?: string[]
  belt?: string
  gym?: string
  location?: string
  size?: number
}

interface Member {
  id: string
  name: string
  belt: string
  avatar: string | null
}

interface Props {
  profile: ProfileData
  matches: CompetitionMatch[]
  members?: Member[]
  error?: boolean
}

export default function GymDetails({
  profile,
  matches,
  members,
  error,
}: Props) {
  const router = useRouter()
  const { token } = useAuth()
  const [isOwner, setIsOwner] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)

  useEffect(() => {
    const checkOwnership = async () => {
      if (!token || !profile) {
        setIsOwner(false)
        return
      }

      try {
        const response = await goodfellasApi.get('/evaluation/resolve/me')
        const me = response.data

        const myUuid = me.profileUserUuid
        const myPhone = me.phoneDigits
        const profileId = String(profile.id)

        const isUuidMatch = myUuid && String(myUuid) === profileId
        const clean = (s: string) => s.replace(/\D/g, '')
        const isPhoneMatch =
          myPhone && clean(String(myPhone)) === clean(profileId)

        if (isUuidMatch || isPhoneMatch) {
          setIsOwner(true)
        } else {
          setIsOwner(false)
        }
      } catch (err) {
        console.debug('Error checking profile ownership', err)
        setIsOwner(false)
      }
    }

    checkOwnership()
  }, [token, profile])

  if (router.isFallback) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <>
        <Head>
          <title>Academia não encontrada | Goodfellas</title>
        </Head>
        <div className="min-h-screen bg-gray-950 pt-32 pb-20">
          <div className="container mx-auto">
            <ProfileNotFound />
          </div>
        </div>
      </>
    )
  }

  const canonical = gymUrl(String(profile.id))
  const ogImage = `${SITE_URL.replace(/\/+$/, '')}/api/og/gym/${profile.id}`
  const description =
    profile.bio || `Perfil da academia ${profile.name} na Goodfellas`

  return (
    <>
      <Head>
        <title>{profile.name} | Goodfellas</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${profile.name} | Goodfellas`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${profile.name} | Goodfellas`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
      </Head>
      <AppleSmartBanner url={canonical} />

      {/* Gate estilo app-first: perfil indexável, interação continua no app */}
      <AppGateOverlay enabled={!isOwner} />

      <GymProfileView
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-restricted-syntax
        profile={profile as any}
        members={members || []}
        matches={matches}
      />

      {isOwner && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={() => setIsEditOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full p-4 shadow-lg transition-colors flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
            <span className="font-bold">Editar Perfil</span>
          </button>
        </div>
      )}

      {isEditOpen && (
        <EditProfileModal
          profile={profile}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            router.replace(router.asPath)
            setIsEditOpen(false)
          }}
        />
      )}
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const id = params?.id as string

  try {
    let apiUrl = API_URL.replace('localhost', 'api').replace('127.0.0.1', 'api')
    if (apiUrl.endsWith('/')) {
      apiUrl = apiUrl.slice(0, -1)
    }

    const res = await fetch(`${apiUrl}/public/profiles/${id}`)

    if (!res.ok) {
      if (res.status === 404) {
        return {
          props: {
            error: true,
            ...(await serverSideTranslations(locale || 'pt', [
              'common',
              'gates',
              'landing',
            ])),
          },
          revalidate: 60,
        }
      }
      throw new Error(`API error: ${res.status}`)
    }

    const json = await res.json()

    if (!json.success || !json.data) {
      return {
        props: {
          error: true,
          ...(await serverSideTranslations(locale || 'pt', [
            'common',
            'gates',
            'landing',
          ])),
        },
        revalidate: 60,
      }
    }

    const { profile, matches, members } = json.data

    // Redirect if not a gym
    if (profile.type !== 'GYM') {
      return {
        redirect: {
          destination: `/fighters/${id}`,
          permanent: false,
        },
      }
    }

    // URL canônica: id legado/alternativo redireciona para o uuid — uma única
    // URL indexada por academia, igual ao deep link.
    if (profile.id && String(profile.id) !== id) {
      return {
        redirect: {
          destination: `/gyms/${profile.id}`,
          permanent: false,
        },
      }
    }

    return {
      props: {
        profile,
        matches: matches || [],
        members: members || [],
        ...(await serverSideTranslations(locale || 'pt', [
          'common',
          'gates',
          'landing',
        ])),
      },
      revalidate: 60,
    }
  } catch (error) {
    console.error('Error fetching gym:', error)
    return {
      props: {
        error: true,
        ...(await serverSideTranslations(locale || 'pt', [
          'common',
          'gates',
          'landing',
        ])),
      },
      revalidate: 10,
    }
  }
}
